<template>
  <transition name="expand-y">
    <div
      v-if="currentReservationData && modifyBookingPropData"
      class="modify-detail component"
      :class="[{ 'multi-room': isMultiRoomReservation }]"
    >
      <div class="modify-detail__header container">
        <div
          v-if="cancelReservationError"
          class="modify-detail__cancel-error"
        >
          <div class="modify-detail__cancel-error-info-block">
            <div class="modify-detail__cancel-error-title">
              <span class="cancel-icon" />
              <span>{{ modifyReservationCancelErrorMessageTitle }}</span>
            </div>
            <div class="modify-detail__cancel-error-message">
              {{ cancelReservationErrorMessage }}
            </div>
          </div>
          <div
            class="close-btn icon-close-v2"
            @click="closeCancelErrorMessage"
            @keydown.enter.prevent="closeCancelErrorMessage"
            @keydown.space.prevent="closeCancelErrorMessage"
          />
        </div>
        <transition name="expand-y">
          <div
            v-if="isMultiRoomReservation && selectedReservationsToCancel"
            class="modify-detail__status-success cancel"
          >
            <div
              v-for="(item, index) in selectedReservationsToCancelItems"
              :key="`selectedReservationToCancel-${item.id}`"
              class="success-wrapper"
            >
              <span class="success-icon" />
              <div class="title">
                {{ ModifyReservationRoomTitle }} #{{ item.roomNumberOrder }}:
                {{ item.roomStays[0].roomName }}
                {{ ModifyReservationCancelRoomSuccessDescription }}
              </div>
            </div>
          </div>
        </transition>
        <div
          v-if="allReservationsAreCancelled && !cancelReservationErrorMessage"
          class="modify-detail__status cancel"
        >
          <div class="modify-detail__status-info-block">
            <div class="modify-detail__status-title">
              <span class="cancel-icon" />
              <span>{{ ModifyReservationCancelTitle }}</span>
            </div>
          </div>
        </div>

        <transition name="expand-y">
          <div
            v-if="showReservationModifiedSuccess"
            class="modify-detail__status-success modify"
          >
            <div class="success-wrapper">
              <span class="success-icon" />
              <div class="title">
                {{ ModifyReservationConfirmationSuccessDescription }}
              </div>
            </div>
          </div>
        </transition>
        <div class="modify-detail__header-back-to-lookup">
          <a
            v-if="!IsReservationMyStaysEntry"
            :href="ModifyReservationLookupPageUrl"
          >{{ ModifyReservationLookUp }}</a>
          <a
            v-if="IsReservationMyStaysEntry"
            :href="ModifyReservationMyStaysPageUrl"
          >{{ ModifyReservationMemberDashboard }}</a>
        </div>
        <div class="modify-detail__header-wrapper">
          <!--start div for print-only-->
          <!--TODO - Needs property logo - to be discussed-->
          <img
            :src="propertyLogoImageSrc + '?fmt=png-alpha'"
            :alt="propertyLogoImageAlt"
            class="modify-detail__header-print-logo print-only"
          >
          <!--end div for print-only-->
          <div class="modify-detail__header-title-block">
            <!--start div for print-only-->
            <span class="modify-detail__header-print-title print-only">{{
              PropertyName
            }}</span>
            <!--end div for print-only-->
            <div class="modify-detail__header-title">
              {{ ModifyReservationPageTitle }}
            </div>
            <div
              class="modify-detail__badge"
              :class="[
                singleRoomStatus === 'Committed'
                  ? '--committed'
                  : '--cancelled',
              ]"
            >
              <span v-if="singleRoomStatus === 'Committed'">{{
                tc("booking.modifyReservationStatusConfirmed")
              }}</span>
              <span v-if="singleRoomStatus === 'Cancelled'">{{
                tc("booking.modifyReservationStatusCancelled")
              }}</span>
            </div>
          </div>
          <div class="utility-actions-wrapper">
            <div
              class="modify-detail__badge"
              :class="[
                singleRoomStatus === 'Committed'
                  ? '--committed'
                  : '--cancelled',
              ]"
            >
              <span v-if="singleRoomStatus === 'Committed'">{{
                tc("booking.modifyReservationStatusConfirmed")
              }}</span>
              <span v-if="singleRoomStatus === 'Cancelled'">{{
                tc("booking.modifyReservationStatusCancelled")
              }}</span>
            </div>
            <utility-actions v-if="!allReservationsAreCancelled" />
          </div>
        </div>
      </div>
      <div class="modify-detail__overview">
        <div class="container">
          <div class="modify-detail__overview-hotel">
            {{ PropertyName }}
          </div>
          <div class="modify-detail__overview-wrapper">
            <div
              v-if="FanId"
              class="modify-detail__overview-member-info"
            >
              <div>{{ ModifyReservationMemberLabel }}</div>
              <div class="modify-detail__overview-highlight">
                {{ FanId }}
              </div>
            </div>
            <div class="modify-detail__overview-itinerary">
              <div v-if="!isMultiRoomReservation">
                {{ ModifyReservationItineraryTitle }}
              </div>
              <div>
                <span v-if="isMultiRoomReservation">{{
                  ModifyReservationItineraryTitle
                }}</span>
                <span class="modify-detail__overview-highlight">{{
                  ModifyReservationItineraryNumber
                }}</span>
              </div>
            </div>
            <div class="modify-detail__overview-confirmations">
              <div
                v-for="(item, index) in sortedReservations"
                v-if="currentReservationsList"
                :key="`sortedReservation-${item.id}`"
                class="confirmation-number"
              >
                <div>
                  <span
                    v-if="isMultiRoomReservation"
                  >Room {{ item.roomNumberOrder }}</span>
                  <span v-if="item.status === 'Committed'">{{
                    ModifyReservationConfirmationTitle
                  }}</span>
                  <span
                    v-if="item.status === 'Cancelled'"
                  >{{ ModifyReservationCancellationDescription }} #</span>
                  <span class="modify-detail__overview-highlight">{{
                    item.id
                  }}</span>
                </div>
                <div>
                  {{ ModifyReservationDateTitle }}
                  <span class="modify-detail__overview-highlight">
                    {{ getReservationDateRange(item) }}
                  </span>
                </div>
              </div>
            </div>
            <div
              tabindex="0"
              class="modify-detail__overview-property-details"
              @click="togglePropertyMap"
              @keydown.space.prevent="togglePropertyMap"
              @keydown.enter.prevent="togglePropertyMap"
            >
              {{ ModifyReservationPropDetailsTitle }}
              <div
                :class="showPropertyMap ? 'icon-minus-white' : 'icon-add-white'"
              />
            </div>
          </div>
          <transition name="expand-y">
            <modify-property-overview v-if="showPropertyMap" />
          </transition>
        </div>
      </div>
      <div
        class="modify-detail__main container"
        :class="allReservationsAreCancelled ? '--allCancelled' : ''"
      >
        <div class="modify-detail__reservations">
          <div
            v-if="allReservationsAreCancelled"
            class="modify-detail__reservation-cancelled-rooms-title"
          >
            {{ ModifyReservationCancelledRoomDescription }}
          </div>
          <!-- Start Room repeater-->
          <div
            v-for="(reservation, index) in currentReservationsList"
            :key="`${reservation.id}-${reservation.roomStays[0].roomTypes[0].roomTypeCode}`"
            class="modify-detail__reservation"
            :class="reservation.status === 'Cancelled' ? '--cancelled' : ''"
          >
            <div
              v-if="reservation.status === 'Cancelled' && index === firstCancelledIndex() && !allReservationsAreCancelled"
              class="modify-detail__reservation-cancelled-rooms-title"
            >
              {{ ModifyReservationCancelledRoomDescription }}
            </div>
            <div class="modify-detail__room-image">
              <picture
                v-lazyload="{
                  src: roomImageSrc(reservation),
                  set: [
                    { height: '400', breakpoint: 'xs' },
                    { height: '600', breakpoint: 'md' },
                  ],
                  fit: 'crop',
                  align: 'center',
                }"
                class="image-wrapper"
              >
                <img
                  :alt="roomImageSrcAlt(reservation)"
                  class="image modify-detail__image"
                  src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
                >
              </picture>
            </div>
            <div class="modify-detail__room-info">
              <!-- Start Room Info-->
              <div class="modify-detail__room-image print-only">
                <img
                  :alt="roomImageSrcAlt(reservation)"
                  class="image modify-detail__image"
                  :src="roomImageSrc(reservation)"
                />
              </div>
              <div class="room-info-header">
                <div class="modify-detail__room-block">
                  <div class="modify-detail__room-name">
                    {{ ModifyReservationRoomTitle }}
                    {{ reservation.roomNumberOrder }}:
                    {{ reservation.roomStays[0].roomName }}
                  </div>
                </div>
                <div
                  class="modify-detail__badge"
                  :class="[
                    reservation.status === 'Committed'
                      ? '--committed'
                      : '--cancelled',
                  ]"
                >
                  <span v-if="reservation.status === 'Committed'">{{
                    tc("booking.modifyReservationStatusConfirmed")
                  }}</span>
                  <span v-if="reservation.status === 'Cancelled'">{{
                    tc("booking.modifyReservationStatusCancelled")
                  }}</span>
                </div>
                <div class="modify-detail__room-confirmation-block">
                  <span
                    v-if="reservation.status !== 'Cancelled'"
                    class="modify-detail__room-confirmation"
                  >
                    {{ ModifyReservationConfirmationTitle }}
                    {{ reservation.id }}
                  </span>
                  <span
                    v-if="reservation.status === 'Cancelled'"
                    class="modify-detail__room-confirmation"
                  >
                    {{ ModifyReservationConfirmationTitle }}
                    {{ getOriginalReservationId(reservation) }}
                  </span>
                  <span
                    v-if="reservation.status === 'Cancelled'"
                    class="modify-detail__cancellation-id"
                  >
                    {{ ModifyReservationCancellationDescription }} #
                    {{ getCancellationId(reservation) }}
                  </span>
                </div>
              </div>
              <div class="modify-detail__guest-rate-wrapper">
                <div class="modify-detail__guest-header">
                  <div class="modify-detail__guest-title">
                    {{ ModifyReservationGuestInfoTitle }}
                  </div>
                  <div class="modify-detail__guest-number">
                    {{ reservation.adultsCount }}
                    <span v-if="isGuestsPlural(reservation.adultsCount)">{{
                      tc("noums.adultPlural")
                    }}</span>
                    <span v-if="!isGuestsPlural(reservation.adultsCount)">{{
                      tc("noums.adultSingular")
                    }}</span>
                    <span v-if="reservation.childCount">,</span>
                    <span v-if="reservation.childCount">{{
                      reservation.childCount
                    }}</span>
                    <span
                      v-if="
                        isGuestsPlural(reservation.childCount) &&
                          reservation.childCount
                      "
                    >{{ tc("noums.childPlural") }}</span>
                    <span
                      v-if="
                        !isGuestsPlural(reservation.childCount) &&
                          reservation.childCount
                      "
                    >{{ tc("noums.childSingular") }}</span>
                  </div>
                </div>
                <div
                  v-if="reservation.guests[0]"
                  class="modify-detail__guest_detail"
                >
                  <div class="modify-detail__guest-name">
                    {{
                      reservation.guests[0].profiles.profileInfo.profile
                        .customer.personName.namePrefix[0] +
                        " " +
                        reservation.guests[0].profiles.profileInfo.profile
                          .customer.personName.givenName[0] +
                        " " +
                        reservation.guests[0].profiles.profileInfo.profile
                          .customer.personName.surname
                    }}
                  </div>
                  <div class="modify-detail__guest-email">
                    {{
                      reservation.guests[0].profiles.profileInfo.profile
                        .customer.email
                    }}
                  </div>
                  <div class="modify-detail__guest-phone">
                    <span
                      v-if="phoneLocationType"
                    >+ {{ phoneLocationType }}
                      {{
                        reservation.guests[0].profiles.profileInfo.profile
                          .customer.telephone.phoneNumber
                      }}</span>
                    <span v-if="!phoneLocationType">{{
                      reservation.guests[0].profiles.profileInfo.profile
                        .customer.telephone.phoneNumber
                    }}</span>
                  </div>
                  <div
                    v-if="reservation.guests.length > 1"
                    class="modify-detail__additional-guests"
                  >
                    <div class="modify-detail__guest-title">
                      {{ ModifyReservationAdditionalGuestFormTitle }}
                    </div>
                    <div
                      v-for="(guest, index) in additionalGuests(reservation.guests)"
                      :key="`${guest}-${index}`"
                      class="modify-detail__guest-name"
                    >
                      {{ guest }}
                    </div>
                  </div>
                </div>
              </div>

              <!--start div for print-only-->
              <div
                v-if="reservation.enhancementServices.length"
                class="modify-detail-privileges -print print-only"
              >
                <div class="modify-detail-privileges__title">
                  {{ ModifyReservationCompPrivilegeTitle }}
                </div>
                <div class="modify-detail-privileges__items">
                  <div
                    v-for="item in reservation.enhancementServices"
                    v-if="reservation.enhancementServices"
                    :key="item.synXisId"
                    class="modify-detail-privileges__item"
                  >
                    <div class="modify-detail-privileges__content">
                      <div class="modify-detail-privileges__heading">
                        {{ item.title }}
                      </div>
                      <!-- <div class="modify-detail-privileges__date">{{ item.description }}</div> -->
                      <!--leaving commented for now-->
                    </div>
                  </div>
                </div>
              </div>
              <!--end div for print-only-->

              <!-- End Room Info-->
              <transition name="expand-y">
                <div
                  v-if="showToggleRoom"
                  :class="`room-wrapper room-wrapper-${index} ${
                    isMultiRoomReservation && reservation.status === 'Cancelled'
                      ? '-hidden'
                      : ''
                  }`"
                >
                  <!-- Start Priviledges-->
                  <div
                    v-if="reservation.enhancementServices.length"
                    class="modify-detail-privileges"
                  >
                    <div class="modify-detail-privileges__title">
                      {{ ModifyReservationCompPrivilegeTitle }}
                    </div>
                    <div class="modify-detail-privileges__items">
                      <div
                        v-for="item in reservation.enhancementServices"
                        v-if="reservation.enhancementServices"
                        :key="item.synXisId"
                        class="modify-detail-privileges__item"
                      >
                        <div class="modify-detail-privileges__image-wrapper">
                          <picture
                            v-lazyload="{
                              src: item.imageSrc,
                              set: [{ height: '200', breakpoint: 'xs' }],
                              fit: 'crop',
                              align: 'center',
                            }"
                            class="image-wrapper"
                          >
                            <img
                              :alt="item.title"
                              class="image modify-detail__image"
                              src="data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw=="
                            >
                          </picture>
                        </div>
                        <div class="modify-detail-privileges__content">
                          <div class="modify-detail-privileges__heading">
                            {{ item.title }}
                          </div>
                          <!-- <div class="modify-detail-privileges__date">{{ item.description }}</div> -->
                          <!--leaving commented for now-->
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- End Priviledges-->
                  <!-- Start Reservation Payment -->
                  <div class="modify-detail__payment">
                    <div class="modify-detail__payment-wrapper">
                      <div class="modify-detail__payment-method">
                        <div class="modify-detail__payment-title">
                          {{ ModifyReservationPaymentTitle }}
                        </div>
                        <div
                          v-if="isDirectBill"
                          class="modify-detail__payment-info"
                        >
                          <div class="modify-detail__payment-type direct-bill">
                            <span>{{ tc("booking.modifyDirectBillLabel") }}</span>
                          </div>
                        </div>
                        <div
                          v-else
                          class="modify-detail__payment-info"
                        >
                          <div
                            :class="creditCard(reservation).type"
                            class="modify-detail__payment-logo"
                          />
                          <div class="modify-detail__payment-type">
                            <span>{{ tc("booking.modifyCardEnd") }} </span>{{ creditCard(reservation).number }}
                          </div>
                        </div>
                      </div>
                      <div class="modify-detail__rate-taxes-block">
                        <div class="modify-detail__rate">
                          <div class="best-rate-label">
                            <div class="best-rate-label-name">
                              {{ reservation.ratePlanNameSynXis }}
                            </div>
                            <div class="best-rate-price-wrapper">
                              <div class="">
                                {{ avgPerNight(reservation.prices.avgPrice) }}
                              </div>
                              <div class="best-rate-avg-per-night">
                                {{ ModifyReservationRateAvgTitle }}
                              </div>
                            </div>
                          </div>
                          <div class="total-nights-rate">
                            <div class="total-nights-rate__label">
                              {{ reservation.prices.nightlyRates.length }}
                              <span v-if="isNightPlural(reservation)">{{
                                tc("noums.nightPlural")
                              }}</span>
                              <span v-else>{{
                                tc("noums.nightSingular")
                              }}</span>
                            </div>
                            <div class="total-nights-rate__price">
                              {{
                                getDisplayPrice({
                                  price:
                                    reservation.roomStays[0].total
                                      .amountBeforeTax,
                                  fromCurrency: currencyCode,
                                  toCurrency: orSelected(currencyCode),
                                  withIsoCode: true,
                                  isoAfter: true,
                                })
                              }}
                            </div>
                          </div>
                          <div>
                            <toggle-details>
                              <div slot-scope="{ opened, toggle, isMounted }">
                                <div
                                  class="detailed-content"
                                  :class="[{ opened }]"
                                >
                                  <div class="activator">
                                    <div
                                      role="button"
                                      tabindex="0"
                                      class="modify-detail__rate-toggle"
                                      :aria-expanded="
                                        opened ? 'true' : 'false'
                                      "
                                      @click="toggle"
                                      @keydown.enter.prevent="toggle"
                                      @keydown.space.prevent="toggle"
                                    >
                                      <div>
                                        {{ tc("booking.modifyDetailsLabel") }}
                                      </div>
                                      <div
                                        :class="
                                          opened ? 'icon-minus' : 'icon-add'
                                        "
                                      />
                                    </div>
                                  </div>

                                  <!--start div for print-only-->
                                  <div class="print-only">
                                    <div class="modify-detail__rate-list">
                                      <div
                                        v-for="(item, index) in reservation.prices.nightlyRates"
                                        :key="`${item.date}-${index}`"
                                        class="modify-detail__rate-item"
                                      >
                                        <div class="modify-detail__rate-date">
                                          {{ bestRateDate(item.date) }}
                                        </div>
                                        <div class="modify-detail__rate-price">
                                          {{
                                            getDisplayPrice({
                                              price: item.price,
                                              fromCurrency: currencyCode,
                                              toCurrency:
                                                orSelected(currencyCode),
                                              withIsoCode: true,
                                              isoAfter: true,
                                            })
                                          }}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <!--end div for print-only-->

                                  <div
                                    class="modify-detail__rate-group"
                                    :class="{ opened: opened }"
                                  >
                                    <div
                                      v-if="opened"
                                      class="modify-detail__rate-list"
                                    >
                                      <div
                                        v-for="(item, index) in reservation.prices.nightlyRates"
                                        :key="`${item.date}-${index}`"
                                        class="modify-detail__rate-item"
                                      >
                                        <div class="modify-detail__rate-date">
                                          {{ bestRateDate(item.date) }}
                                        </div>
                                        <div class="modify-detail__rate-price">
                                          {{
                                            getDisplayPrice({
                                              price: item.price,
                                              fromCurrency: currencyCode,
                                              toCurrency:
                                                orSelected(currencyCode),
                                              withIsoCode: true,
                                              isoAfter: true,
                                            })
                                          }}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </toggle-details>
                          </div>
                        </div>
                        <div class="modify-detail__add-ons">
                          <add-ons-overview
                            v-if="reservation.addOns && reservation.addOns.length > 0"
                            :add-ons="reservation.addOns"
                            :currency-code="currencyCode"
                          />
                        </div>
                        <div class="modify-detail__taxes">
                          <div class="modify-detail__taxes-header">
                            <div class="modify-detail__taxes-label">
                              {{ ModifyReservationTaxesTitle }}
                              <span class="modify-detail__taxes-total">
                                {{ calculateRoomTotalTaxesFees(reservation) }}
                              </span>
                            </div>
                            <div class="modify-detail__taxes-add-ons">
                              <add-ons-taxes-overview
                                v-if="reservation.addOns && reservation.addOns.length > 0"
                                :add-ons="reservation.addOns"
                                :currency-code="currencyCode"
                              />
                            </div>
                          </div>
                          <div>
                            <toggle-details>
                              <div slot-scope="{ opened, toggle, isMounted }">
                                <div
                                  class="detailed-content"
                                  :class="[{ opened }]"
                                >
                                  <div class="activator">
                                    <div
                                      role="button"
                                      tabindex="0"
                                      class="modify-detail__taxes-toggle"
                                      :aria-expanded="
                                        opened ? 'true' : 'false'
                                      "
                                      @click="toggle"
                                      @keydown.enter.prevent="toggle"
                                      @keydown.space.prevent="toggle"
                                    >
                                      <div class="details">
                                        {{ tc("booking.modifyDetailsLabel") }}
                                      </div>
                                      <div
                                        :class="
                                          opened ? 'icon-minus' : 'icon-add'
                                        "
                                      />
                                    </div>
                                  </div>

                                  <!--start div for print-only-->
                                  <div class="print-only">
                                    <div
                                      v-for="(item, index) in sumUniqueTaxCodes(reservation.total.taxesFees)"
                                      :key="`taxes-print-${item.code}-${index}`"
                                      class="modify-detail__taxes-list"
                                    >
                                      <div class="modify-detail__taxes-item">
                                        <div class="modify-detail__taxes-name">
                                          {{ item.description }}
                                        </div>
                                        <div
                                          class="modify-detail__taxes-amount"
                                        >
                                          {{
                                            getDisplayPrice({
                                              price: item.price,
                                              fromCurrency: currencyCode,
                                              toCurrency:
                                                orSelected(currencyCode),
                                              withIsoCode: true,
                                              isoAfter: true,
                                            })
                                          }}
                                        </div>
                                      </div>
                                    </div>
                                    <div
                                      v-if="addOnsTotalTaxes(reservation.addOns) > 0"
                                      class="modify-detail__taxes-list"
                                    >
                                      <div class="modify-detail__taxes-item">
                                        <div class="modify-detail__taxes-name">
                                          {{ addonTaxesTotalLabel }}
                                        </div>
                                        <div class="modify-detail__taxes-amount">
                                          {{
                                            getDisplayPrice({
                                              price: addOnsTotalTaxes(reservation.addOns),
                                              fromCurrency: currencyCode,
                                              toCurrency:
                                                orSelected(currencyCode),
                                              withIsoCode: true,
                                              isoAfter: true,
                                            })
                                          }}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <!--end div for print-only-->

                                  <div
                                    class="modify-detail__taxes-group"
                                    :class="{ opened: opened }"
                                  >
                                    <transition-group name="expand-y">
                                      <div
                                        v-for="(item, index) in sumUniqueTaxCodes(reservation.total.taxesFees)"
                                        v-if="opened"
                                        :key="`taxes-${item.code}`"
                                        class="modify-detail__taxes-list"
                                      >
                                        <div class="modify-detail__taxes-item">
                                          <div
                                            class="modify-detail__taxes-name"
                                          >
                                            {{ item.description }}
                                          </div>
                                          <div
                                            class="modify-detail__taxes-amount"
                                          >
                                            {{
                                              getDisplayPrice({
                                                price: item.price,
                                                fromCurrency: currencyCode,
                                                toCurrency:
                                                  orSelected(currencyCode),
                                                withIsoCode: true,
                                                isoAfter: true,
                                              })
                                            }}
                                          </div>
                                        </div>
                                      </div>
                                    </transition-group>
                                  </div>
                                </div>
                              </div>
                            </toggle-details>
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- Start MultiRoom Total Per Room -->
                    <div
                      v-if="isMultiRoomReservation"
                      class="modify-detail__payment-total-wrapper"
                    >
                      <div class="modify-detail__total-label">
                        {{ ModifyReservationRoomTotalTitle }}
                      </div>
                      <div class="modify-detail__total-price">
                        {{ getRoomTotalPerRoom(reservation) }}
                      </div>
                    </div>
                    <!-- End MultiRoom Total Per Room -->
                  </div>
                  <!-- End Reservation Payment-->
                </div>
              </transition>
              <!--Cancelled Room Toggle only for MultiRoom-->
              <div
                v-if="
                  reservation.status === 'Cancelled' && isMultiRoomReservation
                "
                class="modify-detail__reservation-room-details-toggle"
                :class="`modify-detail__reservation-room-details-toggle-${index} ${
                  isMultiRoomReservation ? 'multi-room' : ''
                }`"
                @click="toggleCancelledRoom(index)"
                @keydown.space.prevent="toggleCancelledRoom(index)"
                @keydown.enter.prevent="toggleCancelledRoom(index)"
              >
                <span
                  tabindex="0"
                  class="title -hide"
                >{{ ModifyReservationHideDetailsDescription }}
                  <div class="icon-minus" /></span>
                <span
                  tabindex="0"
                  class="title -show"
                >{{ ModifyReservationShowDetailsDescription }}
                  <div class="icon-add" /></span>
              </div>
              <!--Cancelled Room Toggle only for MultiRoom-->
            </div>
          </div>
          <!-- End Room repeater-->
          <div
            v-if="showToggleRoom"
            class="modify-detail__payment-total-wrapper"
            :class="[isMultiRoomReservation ? '--grandTotal' : '']"
          >
            <div class="modify-detail__total-label">
              <span v-if="!isMultiRoomReservation">{{
                ModifyReservationTotalTitle
              }}</span>
              <span v-if="isMultiRoomReservation">{{
                ModifyReservationGrandTotalTitle
              }}</span>
            </div>
            <div class="modify-detail__total-price">
              <span v-if="!isMultiRoomReservation">{{ singleRoomtotal }}</span>
              <span v-if="isMultiRoomReservation">{{ multiRoomTotal }}</span>
            </div>
          </div>
          <!--Cancelled Room Toggle only for SingleRoom-->
          <div
            v-if="allReservationsAreCancelled && !isMultiRoomReservation"
            class="modify-detail__reservation-room-details-toggle"
            :class="!showToggleRoom ? '-closed' : ''"
            @click="showToggleRoom = !showToggleRoom"
            @keydown.space.prevent="showToggleRoom = !showToggleRoom"
            @keydown.enter.prevent="showToggleRoom = !showToggleRoom"
          >
            <span
              class="title -hide"
            >{{ ModifyReservationHideDetailsDescription }}
              <div class="icon-minus" /></span>
            <span
              class="title -show"
            >{{ ModifyReservationShowDetailsDescription }}
              <div class="icon-add" /></span>
          </div>
          <!--Cancelled Room Toggle only for SingleRoom-->
        </div>
        <!-- Start Sidebar-->
        <div class="modify-detail__sidebar">
          <!--start div for print-only-->
          <div class="print-only">
            <!--start div for itinerary and confirmation print-only-->
            <div class="modify-room-overview -itinerary">
              <div class="modify-detail__overview-itinerary">
                <div>
                  {{ ModifyReservationItineraryTitle }}
                  <span>:</span>
                </div>
                <div class="modify-detail__overview-highlight">
                  {{ ModifyReservationItineraryNumber }}
                </div>
              </div>
              <div class="modify-detail__overview-confirmations">
                <div>{{ ModifyReservationConfirmationTitle }}<span>:</span></div>
                <div
                  v-for="(item, index) in currentReservationsList"
                  :key="`confirmation-${item.id}`"
                  class="confirmation-number"
                >
                  <span class="modify-detail__overview-highlight">
                    <template v-if="isMultiRoomReservation">
                      {{ ModifyReservationRoomTitle }}
                      {{ item.roomNumberOrder }}: </template>{{ item.id }}</span>
                </div>
              </div>
            </div>
            <!--end div for itinerary and confirmation print-only-->
          </div>
          <!--end div for print-only-->
          <modify-room-overview
            v-for="(reservations, index) in filteredReservationsByDateRange"
            :key="`room-overview-${index}`"
            :reservations="reservations"
          />

          <!--start div for print-only-->
          <div class="print-only">
            <!--start div for property overview details print-only-->
            <div class="modify-room-overview -property-details">
              <div class="modify-detail__overview-property-details">
                {{ ModifyReservationPropDetailsTitle }}
              </div>
              <modify-property-overview />
            </div>
            <!--end div for property overview details print-only-->

            <!--start div for policies details print-only-->
            <div
              v-if="!showDefaultModifyAndCancelMessage"
              class="modify-room-overview -policies"
            >
              <div class="modify-room-overview__policies-title">
                {{ ModifyReservationPoliciesTitle }}
              </div>
              <div class="modify-room-overview__policies-list">
                <div class="modify-room-overview__policies-label">
                  {{ ModifyReservationGuaranteePolicyTitle }}
                </div>
                <!-- html is off from synxis-->
                <ul
                  :class="`modify-room-overview__policies-content ${indentGuaranteePolicyHtmMarkup}`"
                  v-html="guaranteePolicy"
                />
                <div class="modify-room-overview__policies-label">
                  <span v-if="isMultiRoomReservation">{{ cancelReservationPolicyLabelPlural }}</span>
                  <span v-else>{{ cancelReservationPolicyLabelSingular }}</span>
                </div>

                <div
                  v-for="reservation in currentReservationsList"
                  :key="`policy-print-${reservation.id}`"
                  class="cancel-item list-indent"
                >
                  <div v-if="isMultiRoomReservation">
                    {{ ModifyReservationRoomTitle }} {{ reservation.roomNumberOrder }}:
                    {{ reservation.roomStays[0].roomName }}
                  </div>
                  <ul class="modify-room-overview__policies-content">
                    <li>
                      <div v-html="renderCancelPenaltiesText(reservation.cancelPenaltiesText)" />
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div
              v-if="showDefaultModifyAndCancelMessage"
              class="modify-room-overview -policies"
            >
              <div class="modify-room-overview__policies-title">
                {{ ModifyReservationPoliciesTitle }}
              </div>
              <ul
                :class="`modify-room-overview__policies-content ${indentGuaranteePolicyHtmMarkup}`"
                v-html="guaranteePolicy"
              />
              <div class="modify-room-overview__policies-label">
                <span v-if="isMultiRoomReservation">{{ cancelReservationPolicyLabelPlural }}</span>
                <span v-else>{{ cancelReservationPolicyLabelSingular }}</span>
              </div>
              <div class="modify-room-overview__policies-list">
                <div class="modify-room-overview__policies-content remove-indent">
                  <div
                    v-for="reservation in currentReservationsList"
                    :key="`policy-${reservation.id}`"
                    class="policy-item"
                    :class="[{'single': !isMultiRoomReservation}]"
                  >
                    <div
                      v-if="isMultiRoomReservation"
                      class="policy-room-label"
                    >
                      {{ ModifyReservationRoomTitle }} {{ reservation.roomNumberOrder }}:
                      {{ reservation.roomStays[0].roomName }}
                    </div>
                    <ul
                      class="policy-description list-indent"
                      v-html="renderCancelPenaltiesText(reservation.cancelPenaltiesText)"
                    />
                  </div>
                </div>
              </div>
            </div>
            <!--end div for policies details print-only-->
          </div>
          <!--end div for print-only-->
          <modifyChangeActions />
        </div>
        <!-- End Sidebar-->
      </div>
    </div>
  </transition>
</template>

<script>
  import { mapActions, mapGetters, mapState } from 'vuex'
  import { formatDateZone } from '@utils/date-helpers'
  import isEqual from 'lodash/isEqual'
  import modifyChangeActions from '../modify-change-actions/modify-change-actions'
  import utilityActions from '../utility-actions/utility-actions'
  import modifyRoomOverview from '../modify-room-overview/modify-room-overview.vue'
  import modifyPropertyOverview from '../modify-property-overview/modify-property-overview.vue'
  import AddOnsOverview from '@components/06.modify-reservation/components/modify-reservation-detail/overview/add-ons-overview'
  import AddOnsTaxesOverview from '@components/06.modify-reservation/components/modify-reservation-detail/overview/add-on-taxes-overview.vue'

  export default {
    name: "ModifyReservationDetail",
    components: {
      AddOnsTaxesOverview,
      AddOnsOverview,
      modifyChangeActions,
      utilityActions,
      modifyRoomOverview,
      modifyPropertyOverview,
    },
    inject: ['currencies', 'modifyAddOnsData'],
    data: () => ({
      showPropertyMap: false,
      selectedReservationstoCancellChanged: false,
      showToggleRoom: true,
    }),
    computed: {
      ...mapState('global', { currentLanguage: 'lang' }),
      ...mapState('modifyReservation', [
        'currentReservationData',
        'cancelReservationError',
        'cancelReservationErrorMessage',
        'allReservationsAreCancelled',
        'selectedReservationsToCancel',
        'modifyBookingPropData',
        'showReservationModifiedSuccess',
      ]),
      ...mapGetters('modifyReservation', [
        'currentReservationLookUpData',
        'getBookingTrackingModifyReservation',
        'isMultiRoomReservation',
      ]),
      ...mapGetters('currency', [
        'getDisplayPrice',
        'currencyData',
        'orSelected',
      ]),
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('auth', ['userId', 'haveProfileData']),

      currentReservationsList() {
        return this.currentReservationData?.reservations || []
      },
      singleRoomStatus() {
        return this.currentReservationsList[0].status === 'Committed'
          ? 'Committed'
          : 'Cancelled'
      },
      defaultRoomConfirmationNumber() {
        return this.currentReservationsList[0].id
      },
      PropertyName() {
        return this.currentReservationData.property.name
      },
      FanId() {
        return this.userId ? this.userId : false
      },
      hotelCode() {
        return this.currentReservationData
          ? this.currentReservationData?.reservations[0].hotelCode
          : 'Global'
      },
      ModifyReservationItineraryTitle() {
        return this.modifyBookingPropData?.ModifyReservationItineraryTitle
          ? this.modifyBookingPropData.ModifyReservationItineraryTitle
          : ''
      },
      ModifyReservationItineraryNumber() {
        return this.currentReservationData
          ? this.currentReservationData?.reservations[0].itinerary
          : ''
      },
      startDate() {
        return formatDateZone(
          this.currentReservationsList[0].roomStays[0].startDate,
          null,
          'ddd, D MMM, YYYY'
        )
      },
      endDate() {
        return formatDateZone(
          this.currentReservationsList[0].roomStays[0].endDate,
          null,
          'ddd, D MMM, YYYY'
        )
      },
      taxesFees() {
        return this.currentReservationsList[0].total.taxesFees
      },
      singleRoomtotal() {
        const reservation = this.currentReservationsList[0]
        let addOnsTotal = 0
        if (reservation.addOns && reservation.addOns.length > 0) {
          reservation.addOns.forEach((addOn) => {
            const prices = addOn.excludeKidsFromPriceCalculations ? addOn.totalPriceNoChildren : addOn.totalPrice
            const addOnTotalPrice = prices.reduce((sum, prc) => sum + prc, 0)

            addOnsTotal += addOnTotalPrice
          })
        }

        return this.getDisplayPrice({
          price: reservation.total.total + addOnsTotal,
          fromCurrency: this.currencyCode,
          toCurrency: this.orSelected(this.currencyCode),
          withIsoCode: true,
          isoAfter: true,
        })
      },
      multiRoomTotal() {
        const { reservations } = this.currentReservationData

        const calculateTotal = (items, valueSelector) => items.map(valueSelector).reduce((sum, value) => sum + value, 0)

        const roomsTotal = calculateTotal(
          reservations.filter((item) => item.status === 'Committed'),
          (item) => item.total.total
        )

        const addOnsTotal = calculateTotal(
          reservations.filter((item) => item.addOns?.length > 0),
          (item) => item.addOns.reduce((sum, addOn) => sum + this.addOnTotalPrice(addOn), 0)
        )

        return this.getDisplayPrice({
          price: roomsTotal + addOnsTotal,
          fromCurrency: this.currencyCode,
          toCurrency: this.orSelected(this.currencyCode),
          withIsoCode: true,
          isoAfter: true,
        })
      },
      multiRoomTotalWithoutCurrency() {
        return Math.round(this.currentReservationsList
          .filter((item) => item.status === 'Committed')
          .map((item) => item.total.total)
          .reduce((sum, current) => sum + current, 0))
      },
      multiRoomCancelledTotal() {
        return this.getDisplayPrice({
          price: this.currentReservationsList
            .filter((item) => item.status === 'Cancelled')
            .map((item) => item.total.total)
            .reduce((sum, current) => sum + current, 0),
        })
      },
      multiRoomCancelledTotalWithoutCurrency() {
        return this.currentReservationsList
          .filter((item) => item.status === 'Cancelled')
          .map((item) => item.total.total)
      },
      multiRoomOriginalTotal() {
        return Math.round(this.currentReservationsList
          .map((item) => item.total.total)
          .reduce((sum, current) => sum + current, 0))
      },
      totalTaxes() {
        const totalTaxes = parseFloat(
          this.currentReservationsList
            .flatMap((item) => item.total.taxesFees)
            .map((item) => item.price)
            .reduce((sum, current) => sum + current, 0)
            .toFixed(2)
        )
        return Math.round(totalTaxes)
      },
      totalCommittedTaxes() {
        const totalCommittedTaxes = parseFloat(
          this.currentReservationsList
            .filter((item) => item.status === 'Committed')
            .flatMap((item) => item.total.taxesFees)
            .map((item) => item.price)
            .reduce((sum, current) => sum + current, 0)
            .toFixed(2)
        )
        return Math.round(totalCommittedTaxes)
      },
      currencyCode() {
        return this.currentReservationsList[0].prices.currencyCode
      },
      isDirectBill() {
        return this.currentReservationsList[0].guaranteeCode === 'MO Direct Bill'
      },
      selectedReservationsToCancelItems() {
        if (this.selectedReservationsToCancel) {
          const matchedItems = []
          for (const item of this.currentReservationsList) {
            const matchingObject = this.selectedReservationsToCancel.data.find(
              (obj) => obj.cancelInfoRS.uniqueID.id === item.id
            )
            if (matchingObject) {
              matchedItems.push({
                ...item,
                matchingObject,
              })
            }
          }
          return matchedItems
        }
        return null
      },
      sortedReservations() {
        return this.currentReservationsList.slice().sort((a, b) => {
          const orderA = Number(a.roomNumberOrder)
          const orderB = Number(b.roomNumberOrder)
          return orderA - orderB
        })
      },
      ModifyReservationCancelTitle() {
        return this.modifyBookingPropData?.ModifyReservationCancelTitle
          ? this.modifyBookingPropData.ModifyReservationCancelTitle
          : ''
      },
      ModifyReservationCancelSubTitle() {
        return this.modifyBookingPropData?.ModifyReservationCancelSubTitle
          ? this.modifyBookingPropData.ModifyReservationCancelSubTitle
          : ''
      },
      ModifyReservationRebookCTALabel() {
        return this.modifyBookingPropData?.ModifyReservationRebookCTALabel
          ? this.modifyBookingPropData.ModifyReservationRebookCTALabel
          : ''
      },
      ModifyReservationPageTitle() {
        return this.modifyBookingPropData?.ModifyReservationPageTitle
          ? this.modifyBookingPropData.ModifyReservationPageTitle
          : ''
      },
      ModifyReservationConfirmationTitle() {
        return this.modifyBookingPropData?.ModifyReservationConfirmationTitle
          ? this.modifyBookingPropData.ModifyReservationConfirmationTitle
          : ''
      },
      ModifyReservationDateTitle() {
        return this.modifyBookingPropData?.ModifyReservationDateTitle
          ? this.modifyBookingPropData.ModifyReservationDateTitle
          : ''
      },
      ModifyReservationPropDetailsTitle() {
        return this.modifyBookingPropData?.ModifyReservationPropDetailsTitle
          ? this.modifyBookingPropData.ModifyReservationPropDetailsTitle
          : ''
      },
      ModifyReservationGuestInfoTitle() {
        return this.modifyBookingPropData?.ModifyReservationGuestInfoTitle
          ? this.modifyBookingPropData.ModifyReservationGuestInfoTitle
          : ''
      },
      ModifyReservationAdditionalGuestFormTitle() {
        return this.modifyBookingPropData
          ?.ModifyReservationAdditionalGuestFormTitle
          ? this.modifyBookingPropData.ModifyReservationAdditionalGuestFormTitle
          : ''
      },
      ModifyReservationCompPrivilegeTitle() {
        return this.modifyBookingPropData?.ModifyReservationCompPrivilegeTitle
          ? this.modifyBookingPropData.ModifyReservationCompPrivilegeTitle
          : ''
      },
      ModifyReservationPaymentTitle() {
        return this.modifyBookingPropData?.ModifyReservationPaymentTitle
          ? this.modifyBookingPropData.ModifyReservationPaymentTitle
          : ''
      },
      ModifyReservationTaxesTitle() {
        return this.modifyBookingPropData?.ModifyReservationTaxesTitle
          ? this.modifyBookingPropData.ModifyReservationTaxesTitle
          : ''
      },
      ModifyReservationRoomTotalTitle() {
        return this.modifyBookingPropData?.ModifyReservationRoomTotalTitle
          ? this.modifyBookingPropData.ModifyReservationRoomTotalTitle
          : ''
      },
      ModifyReservationTotalTitle() {
        return this.modifyBookingPropData?.ModifyReservationTotalTitle
          ? this.modifyBookingPropData.ModifyReservationTotalTitle
          : ''
      },
      ModifyReservationGrandTotalTitle() {
        return this.modifyBookingPropData?.ModifyReservationGrandTotalTitle
          ? this.modifyBookingPropData.ModifyReservationGrandTotalTitle
          : ''
      },
      modifyReservationCancelErrorMessageTitle() {
        return this.modifyBookingPropData
          ?.ModifyReservationCancelErrorMessageTitle
          ? this.modifyBookingPropData.ModifyReservationCancelErrorMessageTitle
          : ''
      },
      ModifyReservationMemberLabel() {
        return this.modifyBookingPropData?.ModifyReservationMemberLabel
          ? this.modifyBookingPropData.ModifyReservationMemberLabel
          : ''
      },
      ModifyReservationRoomTitle() {
        return this.modifyBookingPropData?.ModifyReservationRoomTitle
          ? this.modifyBookingPropData.ModifyReservationRoomTitle
          : ''
      },
      // start print only computed for policies
      ModifyReservationPoliciesTitle() {
        return this.modifyBookingPropData?.ModifyReservationPoliciesTitle
          ? this.modifyBookingPropData.ModifyReservationPoliciesTitle
          : ''
      },
      ModifyReservationGuaranteePolicyTitle() {
        return this.modifyBookingPropData?.ModifyReservationGuaranteePolicyTitle
          ? this.modifyBookingPropData.ModifyReservationGuaranteePolicyTitle
          : ''
      },
      showDefaultModifyAndCancelMessage() {
        return !this.anyReservationsCancellable && !this.anyReservationsModifiable
      },
      cancelReservationPolicyLabelSingular() {
        return this.modifyBookingPropData?.CancelReservationPolicyLabelSingular
          ? this.modifyBookingPropData.CancelReservationPolicyLabelSingular
          : ''
      },
      cancelReservationPolicyLabelPlural() {
        return this.modifyBookingPropData?.CancelReservationPolicyLabelPlural
          ? this.modifyBookingPropData.CancelReservationPolicyLabelPlural
          : ''
      },
      anyReservationsModifiable() {
        return this.currentReservationsList.some((reservation) => reservation.isModifiable)
      },
      anyReservationsCancellable() {
        return this.currentReservationsList.some((reservation) => reservation.isCancellable)
      },
      ModifyReservationRateAvgTitle() {
        return this.modifyBookingPropData?.ModifyReservationAveragePerNightTitle
          ? this.modifyBookingPropData.ModifyReservationAveragePerNightTitle
          : ''
      },
      guaranteePolicy() {
        return this.currentReservationsList[0].roomStays[0]
          .ratePlans[0].guarantees[0]
      },
      indentGuaranteePolicyHtmMarkup() {
        const markup = this.currentReservationsList[0].roomStays[0]
          .ratePlans[0].guarantees[0]
        const markupWithHtml = /<li>/.test(markup)
        return markupWithHtml ? '' : 'remove-indent'
      },
      propertyLogoImageSrc() {
        const src = this.currentReservationData.property.propertyIconLogo
        return src || ''
      },
      propertyLogoImageAlt() {
        const alt = this.currentReservationData.property.propertyLogoAlt
        return alt || ''
      },
      /// end print only computed for policies
      ModifyReservationConfirmationSuccessDescription() {
        return this.modifyBookingPropData
          ?.ModifyReservationConfirmationSuccessDescription
          ? this.modifyBookingPropData
              .ModifyReservationConfirmationSuccessDescription
          : ''
      },
      ModifyReservationCancelRoomSuccessDescription() {
        return this.modifyBookingPropData
          ?.ModifyReservationCancelRoomSuccessDescription
          ? this.modifyBookingPropData
              .ModifyReservationCancelRoomSuccessDescription
          : ''
      },
      ModifyReservationCancelledRoomDescription() {
        return this.modifyBookingPropData
          ?.ModifyReservationCancelledRoomsDescription
          ? this.modifyBookingPropData.ModifyReservationCancelledRoomsDescription
          : ''
      },
      ModifyReservationShowDetailsDescription() {
        return this.modifyBookingPropData?.ModifyReservationShowDetailsDescription
          ? this.modifyBookingPropData.ModifyReservationShowDetailsDescription
          : ''
      },
      ModifyReservationHideDetailsDescription() {
        return this.modifyBookingPropData?.ModifyReservationHideDetailsDescription
          ? this.modifyBookingPropData.ModifyReservationHideDetailsDescription
          : ''
      },
      ModifyReservationCancellationDescription() {
        return this.modifyBookingPropData
          ?.ModifyReservationCancellationDescription
          ? this.modifyBookingPropData.ModifyReservationCancellationDescription
          : ''
      },
      ModifyReservationLookupPageUrl() {
        return this.modifyBookingPropData?.ModifyReservationLookupPageUrl
          ? `/${this.currentLanguage}/${this.modifyBookingPropData.ModifyReservationLookupPageUrl}`
          : ''
      },
      ModifyReservationMyStaysPageUrl() {
        return this.modifyBookingPropData?.ModifyReservationMyStaysPageUrl
          ? `/${this.currentLanguage}/${this.modifyBookingPropData.ModifyReservationMyStaysPageUrl}`
          : ''
      },
      ModifyReservationLookUp() {
        return this.modifyBookingPropData?.ModifyReservationLookUp
          ? this.modifyBookingPropData.ModifyReservationLookUp
          : ''
      },
      ModifyReservationMemberDashboard() {
        return this.modifyBookingPropData?.ModifyReservationMemberDashboard
          ? this.modifyBookingPropData.ModifyReservationMemberDashboard
          : ''
      },
      IsReservationMyStaysEntry() {
        const isMyStayEntry = sessionStorage.getItem(
          'storedReservationMyStaysEntry'
        )
        return !(isMyStayEntry === null || isMyStayEntry === 'false')
      },
      phoneLocationType() {
        // Required for ModifyReservation
        return this.currentReservationData
          ? this.currentReservationsList[0].guests[0].profiles
            .profileInfo.profile.customer.telephone.phoneLocationType
          : null
      },
      filteredReservationsByDateRange() {
        const groups = {}
        this.currentReservationsList.forEach((reservation) => {
          if (reservation.status === 'Committed') {
            const key = `${reservation.roomStays[0].startDate}-${reservation.roomStays[0].endDate}`
            if (!groups[key]) {
              groups[key] = []
            }
            groups[key].push(reservation)
          }
        })

        return Object.values(groups)
      },
      addonTaxesTotalLabel() {
        return this.modifyAddOnsData?.addonsTaxesTotalLabel || ''
      },
    },
    watch: {
      selectedReservationsToCancel(val) {
        if (val) {
          this.selectedReservationstoCancellChanged = true
        }
      },
      multiRoomCancelledTotalWithoutCurrency(val) {
        if (this.selectedReservationstoCancellChanged) {
          this.adobeEventCancelReservations()
          this.selectedReservationstoCancellChanged = false
        }
      },
    },
    async created() {
      await this.registerCurrencies(this.currencies)
    },
    mounted() {
      if (this.showReservationModifiedSuccess) {
        this.readReservationData()
      }

      if (!this.currentReservationData) {
        this.toggleBookingEditMode(false)
        this.$router.push({ name: 'lookup-reservation' })
      } else {
        // Start Session
        this.manageReservationSession()
        this.toggleBookingEditMode(false)
        this.$nextTick(() => {
          this.triggerViewResAdobeEvent()
        })
      }
    },
    methods: {
      ...mapActions('modifyReservation', [
        'readReservation',
        'resetCancelErrorMessage',
        'manageReservationSession',
        'toggleBookingEditMode',
        'removeReservationModifySuccess',
      ]),
      ...mapActions('auth', ['getProfile']),
      ...mapActions('currency', ['registerCurrencies']),
      async initUser() {
        if (this.user && !this.haveProfileData) {
          await this.getProfile()
        }
      },
      async readReservationData() {
        const oldReservationData = this.currentReservationData
        const data = await this.readReservation({
          lookUpDataObject: this.currentReservationLookUpData
        })
        if (!data.reservations) {
          this.$router.push({ name: 'lookup-reservation' })
        }
        if (data && this.showReservationModifiedSuccess) {
          this.compareReservationsAfterModification(oldReservationData)
        }
        this.removeReservationModifySuccess()
      },
      triggerViewResAdobeEvent() {
        if (
          this.showReservationModifiedSuccess
          || this.selectedReservationstoCancellChanged
        ) {
          return
        }
        this.$track(
          this.getBookingTrackingModifyReservation(
            {
              name: 'home >> modify >> find-reservation >> details',
              hotelCode: this.hotelCode,
              siteSection: 'find - details',
            },
            { event: 'viewRes' }
          )
        )
        this.$track(
          this.getBookingTrackingModifyReservation(undefined,
            {
              event: 'bookingStateChange',
              bookingEngine: {
                bookingStepNumber: '0.1',
                bookingStepName: 'View Reservation',
                bookingEngineVersion: '2',
                bookingFlow: this.isMultiRoomReservation ? 'multiRoomMod' : 'singleRoomMod'
              }
            })
        )
      },
      togglePropertyMap() {
        if (!this.showPropertyMap) {
          this.showPropertyMap = true;
        } else {
          this.showPropertyMap = false;
        }
      },
      bestRateDate(date) {
        return formatDateZone(date, null, 'D MMM, YYYY')
      },
      calculateRoomTotalTaxesFees(reservation) {
        const roomTaxes = reservation.total.taxesFees.map((item) => item.price)
          .reduce((total, current) => total + current, 0)

        const addOnsTaxesTotal = reservation.addOns?.reduce(
          (total, addOn) => {
            const countNights = addOn.timeSpan.length || 1
            const taxPrice = (addOn.excludeKidsFromPriceCalculations
              ? addOn.taxes.totalNoChildTaxPricePaidForAddOnByUser
              : addOn.taxes.totalTaxPricePaidForAddOnByUser) * countNights
            return total + taxPrice
          },
          0
        ) || 0

        return this.getDisplayPrice({
          price: roomTaxes + addOnsTaxesTotal,
          fromCurrency: this.currencyCode,
          toCurrency: this.orSelected(this.currencyCode),
          withIsoCode: true,
          isoAfter: true,
        })
      },
      calculateRoomTotal(roomTotal) {
        return this.getDisplayPrice({
          price: roomTotal
            .map((item) => item.price)
            .reduce((total, current) => total + current, 0),
          fromCurrency: this.currencyCode,
          toCurrency: this.orSelected(this.currencyCode),
          withIsoCode: true,
          isoAfter: true,
        })
      },
      additionalGuests(guests) {
        return guests
          .filter((guest) => guest.isPrimary === false)
          .map((guest) => {
            const firstName = guest.profiles.profileInfo.profile.customer.personName.givenName[0]
            const lastName = guest.profiles.profileInfo.profile.customer.personName.surname
            return firstName ? `${firstName} ${lastName}` : lastName
          })
      },
      closeCancelErrorMessage() {
        this.resetCancelErrorMessage()
      },
      isGuestsPlural(count) {
        return Number(count) > 1
      },
      isNightPlural(reservation) {
        return reservation.prices.nightlyRates.length > 1
      },
      roomImageSrc(item) {
        try {
          const { src } = item.roomStays[0].roomTypes[0].images[0]
          return src || ''
        } catch (e) {
          return ''
        }
      },
      roomImageSrcAlt(item) {
        try {
          const srcAlt = item.roomStays[0].roomTypes[0].images[0].alt
          return srcAlt || ''
        } catch (e) {
          return ''
        }
      },
      avgPerNight(avgPerNight) {
        return this.getDisplayPrice({
          price: avgPerNight,
          fromCurrency: this.currencyCode,
          toCurrency: this.orSelected(this.currencyCode),
          withIsoCode: true,
          isoAfter: true,
        })
      },
      getRoomTotalPerRoom(reservation) {
        let addOnsTotal = 0
        if (reservation.addOns && reservation.addOns.length > 0) {
          reservation.addOns.forEach((addOn) => {
            const prices = addOn.excludeKidsFromPriceCalculations ? addOn.totalPriceNoChildren : addOn.totalPrice
            const addOnTotalPrice = prices.reduce((sum, prc) => sum + prc, 0)

            addOnsTotal += addOnTotalPrice
          })
        }

        return this.getDisplayPrice({
          price: reservation.total.total + addOnsTotal,
          fromCurrency: this.currencyCode,
          toCurrency: this.orSelected(this.currencyCode),
          withIsoCode: true,
          isoAfter: true,
        })
      },
      getOriginalReservationId(reservation) {
        return reservation.reservationIds
          .flatMap((item) => item)
          .filter((item) => item.source === 'RedX' && item.type === '14')
          .map((item) => item.value)
          .join('')
      },
      getCancellationId(reservation) {
        return reservation.reservationIds
          .flatMap((item) => item)
          .filter((item) => item.source === 'RedX' && item.type === '15')
          .map((item) => item.value)
          .join('')
      },
      calculateDaysBetweenDates(dateString) {
        const specificDate = new Date(dateString)
        const currentDate = new Date()

        const differenceInTime = specificDate.getTime() - currentDate.getTime()
        const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24))

        return differenceInDays.toString()
      },
      // SBE-56 Adobe Tracking Cancellation Event
      adobeEventCancelReservations() {
        const currentDate = new Date()
        const reservationData = this.currentReservationsList
        const selectedConfirmationNumbers = this.selectedReservationsToCancel.data.map(
            (reservation) => reservation.cancelInfoRS.uniqueID.id
        )

        const cancelledReservationsTotal = reservationData
          .filter((reservation) => selectedConfirmationNumbers.includes(reservation.id))
          .map((item) => item.total.total)
          .reduce((sum, current) => sum + current, 0)

        const cancelledReservationsTaxes = reservationData
          .filter((reservation) => selectedConfirmationNumbers.includes(reservation.id))
          .flatMap((item) => item.total.taxesFees)
          .map((item) => item.price)
          .reduce((sum, current) => sum + current, 0)
          .toFixed(2)

        let cancelTotal = reservationData
            .map((item) => item.total.total)
          .reduce((sum, current) => sum + current, 0) - this.totalTaxes
        cancelTotal = Math.round(cancelTotal).toString()
        const cancelTotalTaxes = this.totalTaxes.toString()

        const commonDetails = {
          itineraryID: this.ModifyReservationItineraryNumber,
          hotelCode: reservationData[0].hotelCode,
          checkIn: this.startDate,
          checkOut: this.endDate,
          date: formatDateZone(currentDate, null, 'ddd, D MMM, YYYY'),
          roomQty: this.currentReservationsList.length.toString(),
          tax: cancelTotalTaxes,
          currency: this.currencyCode,
          cancellationRevLost: (
            cancelledReservationsTotal - cancelledReservationsTaxes
          ).toFixed(2),
          total: cancelTotal,
        }

        const rooms = reservationData
          .filter((reservation) => selectedConfirmationNumbers.includes(reservation.id))
          .map((reservation) => ({
            confirmationID: reservation.reservationIds
              .flatMap((item) => item)
              .filter((item) => item.source === 'RedX' && item.type === '14')
              .map((item) => item.value)
              .join(''),
            type: reservation.roomStays[0].roomName,
            rate: reservation.ratePlanNameSynXis,
            numNights: reservation.prices.nightlyRates.length.toString(),
            numAdults: reservation.adultsCount.toString(),
            numChildren: reservation.childCount.toString(),
            daysToCheckIn: this.calculateDaysBetweenDates(this.startDate),
            price: reservation.roomStays[0].total.amountBeforeTax.toString(),
            tax: Math.round(
              reservation.total.taxesFees
                .map((item) => item.price)
                .reduce((sum, current) => sum + current, 0)
            ).toString(),
            enhancements: reservation.enhancementServices
              ? reservation.enhancementServices
                .map((item) => item.title)
                .join(' > ')
              : '',
          }))

        // Construct the final transformedReservations object
        const transformedReservations = {
          reservation: commonDetails,
          rooms,
        }

        const baseTaggingData = transformedReservations

        this.$track(
          this.getBookingTrackingModifyReservation(
            {
              name: 'home >> cancel-confirmation',
              hotelCode: this.hotelCode,
              siteSection: 'cancel confirmation',
            },
            {
              event: 'cancelRes',
              reservation: baseTaggingData.reservation,
              rooms: baseTaggingData.rooms,
            }
          )
        )

        this.$track(
          this.getBookingTrackingModifyReservation(undefined,
            {
             event: 'bookingStateChange',
             bookingEngine: {
               bookingStepNumber: '1',
               bookingStepName: 'Confirmation',
               bookingEngineVersion: '2',
               bookingFlow: this.isMultiRoomReservation ? 'multiRoomMod' : 'singleRoomMod'
             }
            })
          )
      },
      compareReservationsAfterModification(pastReservationData) {
        if (!this.showReservationModifiedSuccess) {
          return null
        }
        const changedRooms = pastReservationData.reservations.filter((oldRes) => {
          const currentRes = this.currentReservationsList.find(
            (res) => res.id === oldRes.id
          )
          if (!currentRes) {
            return false // No matching reservation found in the current data
          }
          // Check for changes in roomStays
          const roomStaysChanged = !isEqual(
            oldRes.roomStays,
            currentRes.roomStays
          )
          // Check for changes in ratePlanNameSynXis
          const ratePlanNameSynXisChanged = oldRes.ratePlanNameSynXis !== currentRes.ratePlanNameSynXis
          // Return true if either roomStays or ratePlanNameSynXis changed
          return roomStaysChanged || ratePlanNameSynXisChanged
        })

        const changedGuests = pastReservationData.reservations.filter(
          (oldRes) => {
            const guestsChanged = this.currentReservationsList.find(
              (res) => res.id === oldRes.id
            )
            return (
              guestsChanged
              && !isEqual(
                oldRes.guests.map((guest) => guest.profiles),
                guestsChanged.guests.map((guest) => guest.profiles)
              )
            )
          }
        )

        const changedEnhancements = pastReservationData.reservations.filter(
          (oldRes) => {
            const currentRes = this.currentReservationsList.find(
              (res) => res.id === oldRes.id
            )
            if (!currentRes) return false

            if (
              oldRes.enhancementServices.length === 0
              && currentRes.enhancementServices.length === 0
            ) {
              return false
            }

            const oldEnhancements = oldRes.enhancementServices.map(
              ({ title, description }) => ({ title, description })
            )
            const currentEnhancements = currentRes.enhancementServices.map(
              ({ title, description }) => ({ title, description })
            )

            return !isEqual(oldEnhancements, currentEnhancements)
          }
        )

        const currentDate = new Date()
        const reservationData = this.currentReservationsList

        // Past Reservation vs New Reservation Revenue
        let modifiedTotal = this.multiRoomTotalWithoutCurrency - this.totalCommittedTaxes
        modifiedTotal = Math.round(modifiedTotal)

        let pastModifiedTotal = pastReservationData.reservations
          .filter((item) => item.status === 'Committed')
          .map((item) => item.total.total)
          .reduce((sum, current) => sum + current, 0)
        pastModifiedTotal = Math.round(pastModifiedTotal)

        let pastModifieldTotalTaxes = pastReservationData.reservations
          .filter((item) => item.status === 'Committed')
          .flatMap((item) => item.total.taxesFees)
          .map((item) => item.price)
          .reduce((sum, current) => sum + current, 0)
          .toFixed(2)

        pastModifieldTotalTaxes = Math.round(pastModifieldTotalTaxes)

        const pastTotalBeforeTaxes = pastModifiedTotal - pastModifieldTotalTaxes

        // SBE-57 Adobe Tracking Modify Rooms & Hotels Event
        if (changedRooms.length > 0) {
          const reservationsModifyRoomsRootData = {
            itineraryID: this.ModifyReservationItineraryNumber,
            hotelCode: reservationData[0].hotelCode,
            checkIn: this.startDate,
            checkOut: this.endDate,
            date: formatDateZone(currentDate, null, 'ddd, D MMM, YYYY'),
            roomQty: this.currentReservationsList.length.toString(),
            tax: this.totalCommittedTaxes.toString(),
            currency: this.currencyCode,
            total: modifiedTotal.toString(),
            modificationRevChange: (
              modifiedTotal - pastTotalBeforeTaxes
            ).toString(),
            promoCode:
              this.currentReservationsList[0].roomStays[0]
                .promotionCode || '',
          }

          const roomsModifyData = changedRooms.map(
            (changedReservation, index) => {
              // Find the matching reservation from currentReservationsList
              const matchedReservation = this.currentReservationsList.find(
                (currentRes) => currentRes.id === changedReservation.id
              )

              if (!matchedReservation) {
                return
              }

              const changeRoomPrice = Math.round(
                matchedReservation.roomStays[0].total.amountBeforeTax
              )
              const changeRoomtax = Math.round(
                matchedReservation.roomStays[0].total.amountAfterTax
                - matchedReservation.roomStays[0].total.amountBeforeTax
              )

              return {
                confirmationID: matchedReservation.id,
                type: matchedReservation.roomStays[0].roomName,
                rate: matchedReservation.ratePlanNameSynXis,
                numNights:
                  matchedReservation.prices.nightlyRates.length.toString(),
                numAdults: matchedReservation.adultsCount.toString(),
                numChildren: matchedReservation.childCount.toString(),
                daysToCheckIn: this.calculateDaysBetweenDates(this.startDate),
                price: changeRoomPrice.toString(),
                tax: changeRoomtax.toString(),
                enhancements: matchedReservation.enhancementServices
                  ? matchedReservation.enhancementServices
                    .map((item) => item.title)
                    .join(' > ')
                  : '',
                prevEnhancements: pastReservationData.reservations
                  ? pastReservationData.reservations
                    .filter(
                      (pastReservation) => pastReservation.id === matchedReservation.id
                    )
                    .flatMap((item) => item.enhancementServices)
                    .map((item) => item.title)
                    .join(' > ')
                  : '',
              }
            }
          )

          const transformedModifyRoomsReservations = {
            reservation: reservationsModifyRoomsRootData,
            rooms: roomsModifyData,
          }

          this.$track(
            this.getBookingTrackingModifyReservation(
              {
                name: 'home >> modify-confirmation',
                hotelCode: this.hotelCode,
                siteSection: 'modify confirmation',
              },
              {}
            )
          )

          this.$track(
            this.getBookingTrackingModifyReservation(
              {
                name: 'home >> modify-confirmation',
                hotelCode: this.hotelCode,
                siteSection: 'modify confirmation',
              },
              {
                event: 'modifyRes',
                reservation: transformedModifyRoomsReservations.reservation,
                rooms: transformedModifyRoomsReservations.rooms,
              }
            )
          )

          this.$track(
            this.getBookingTrackingModifyReservation(undefined,
              {
                event: 'bookingStateChange',
                bookingEngine: {
                  bookingStepNumber: '4',
                  bookingStepName: 'Confirmation',
                  bookingEngineVersion: '2',
                  bookingFlow: this.isMultiRoomReservation ? 'multiRoomMod' : 'singleRoomMod'
                }
              })
          )
        }
        // SBE-55 Adobe Tracking GuestInfo Event
        if (changedGuests.length > 0) {
          this.$track(
            this.getBookingTrackingModifyReservation(
              {
                name: 'home >> modify-confirmation',
                hotelCode: this.hotelCode,
                siteSection: 'modify confirmation',
              },
              {}
            )
          )
          this.$track(
            this.getBookingTrackingModifyReservation(
              {
                name: 'home >> modify-confirmation',
                hotelCode: this.hotelCode,
                siteSection: 'modify confirmation',
              },
              { event: 'modifyGuestInfo' }
            )
          )
        }
        // SBE-71 Adobe Tracking Complimentary Privileges/Enhancements Event
        if (changedEnhancements.length > 0) {
          const reservationsEnhancementsRootData = {
            itineraryID: this.ModifyReservationItineraryNumber,
            hotelCode: reservationData[0].hotelCode,
            checkIn: this.startDate,
            checkOut: this.endDate,
            date: formatDateZone(currentDate, null, 'ddd, D MMM, YYYY'),
            roomQty: this.currentReservationsList.length.toString(),
            tax: this.totalCommittedTaxes.toString(),
            currency: this.currencyCode,
            total: modifiedTotal.toString(),
            promoCode:
              this.currentReservationsList[0].roomStays[0]
                .promotionCode || '',
          }

          const roomsEnhancementsData = changedEnhancements.map(
            (changedReservation, index) => {
              const matchedReservation = this.currentReservationsList.find(
                (currentRes) => currentRes.id === changedReservation.id
              )

              if (!matchedReservation) {
                return
              }

              const roomEnhancementPrice = Math.round(
                matchedReservation.roomStays[0].total.amountBeforeTax
              )
              const roomEnhancementTax = Math.round(
                matchedReservation.roomStays[0].total.amountAfterTax
                - matchedReservation.roomStays[0].total.amountBeforeTax
              )

              return {
                confirmationID: matchedReservation.id,
                type: matchedReservation.roomStays[0].roomName,
                rate: matchedReservation.ratePlanNameSynXis,
                numNights:
                  matchedReservation.prices.nightlyRates.length.toString(),
                numAdults: matchedReservation.adultsCount.toString(),
                numChildren: matchedReservation.childCount.toString(),
                daysToCheckIn: this.calculateDaysBetweenDates(this.startDate),
                price: roomEnhancementPrice.toString(),
                tax: roomEnhancementTax.toString(),
                enhancements: matchedReservation.enhancementServices
                  ? matchedReservation.enhancementServices
                    .map((item) => item.title)
                    .join(' > ')
                  : '',
                prevEnhancements: pastReservationData.reservations
                  ? pastReservationData.reservations
                    .filter(
                      (pastReservation) => pastReservation.id === matchedReservation.id
                    )
                    .flatMap((item) => item.enhancementServices)
                    .map((item) => item.title)
                    .join(' > ')
                  : '',
              }
            }
          )

          const transformedEnhancementsReservations = {
            reservation: reservationsEnhancementsRootData,
            rooms: roomsEnhancementsData,
          }

          this.$track(
            this.getBookingTrackingModifyReservation(
              {
                name: 'home >> modify-confirmation',
                hotelCode: this.hotelCode,
                siteSection: 'modify confirmation',
              },
              {}
            )
          )

          this.$track(
            this.getBookingTrackingModifyReservation(
              {
                name: 'home >> modify-confirmation',
                hotelCode: this.hotelCode,
                siteSection: 'modify confirmation',
              },
              {
                event: 'modifyCompPrivileges',
                reservation: transformedEnhancementsReservations.reservation,
                rooms: transformedEnhancementsReservations.rooms,
              }
            )
          )
        }
        return true
      },
      toggleCancelledRoom(index) {
        const elementClass = `.room-wrapper-${index}`
        const elementToggleClass = `.modify-detail__reservation-room-details-toggle-${index}`
        const el = document.querySelector(elementClass)
        const elClicked = document.querySelector(elementToggleClass)
        el.classList.toggle('-hidden')
        elClicked.classList.toggle('-closed')
      },
      getReservationDateRange(reservation) {
        return `${formatDateZone(
          reservation.roomStays[0].startDate,
          null,
          'ddd, D MMM, YYYY'
        )} - ${formatDateZone(
          reservation.roomStays[0].endDate,
          null,
          'ddd, D MMM, YYYY'
        )}`
      },
      renderCancelPenaltiesText(cancelPolicyHtml) {
        const markupWithHtml = /<li>/.test(cancelPolicyHtml)
        return markupWithHtml ? cancelPolicyHtml : `<li>${cancelPolicyHtml}</li>`
      },
      sumUniqueTaxCodes(taxesFees) {
        // Use reduce to accumulate taxes, summing amounts by code.
        const summedTaxes = taxesFees.reduce((acc, item) => {
          // If the code already exists in the accumulator, sum the amounts.
          if (acc.hasOwnProperty(item.code)) {
            acc[item.code].price = parseFloat((acc[item.code].price + item.price).toFixed(2))
            acc[item.code].amount = parseFloat((acc[item.code].amount + item.amount).toFixed(2))
          } else {
            // Otherwise, clone the item and add it to the accumulator.
            acc[item.code] = { ...item }
          }
          return acc
        }, {})
        // Convert the accumulated object back to an array for the unique tax entries.
        return Object.values(summedTaxes)
      },
      creditCard(reservation) {
        const fallbackCredit = '1;unknown;0000'
        const splitValues = reservation.guarantee !== ''
          ? reservation.guarantee.split(';')
          : fallbackCredit.split(';')
        return {
          id: splitValues[0],
          type: splitValues[1],
          number: splitValues[2],
        }
      },
      firstCancelledIndex() {
        return this.currentReservationsList.findIndex((reservation) => reservation.status === 'Cancelled')
      },
      addOnsTotalTaxes(addOns) {
        let total = 0
        if (!Array.isArray(addOns)) {
          return 0
        }
        addOns.forEach((addOn) => { total += addOn.taxes.totalTax })
        return total
      },
      addOnTotalPrice(addOn) {
        const prices = addOn.excludeKidsFromPriceCalculations ? addOn.totalPriceNoChildren : addOn.totalPrice
        return prices.reduce((sum, prc) => sum + prc, 0)
      },
    },
  };
</script>
